<template>
  <div>
    <b-row>
      <b-col cols="12" md="8">
        <b-form-group
          class="col px-0"
          label-cols-sm="2"
          label="แพทย์:"
          label-align-sm="right"
          label-class="font-weight-bold"
        >
          <b-input-group>
            <multiselect
              class="col p-0"
              v-model="selectedDoctor"
              :options="getDoctorList"
              :multiple="true"
              :limit="5"
              placeholder="พิมพ์ค้นหาแพทย์"
              track-by="nameTh"
              label="fullName"
              selectLabel=""
              selectedLabel=""
              deselectLabel="คลิกเพื่อลบออก"
            >
              <span slot="noResult">ไม่พบรายการ กรุณาเปลี่ยนคำค้นหา</span>
            </multiselect>
            <b-input-group-append>
              <b-button variant="info" @click="selectedDoctor = getDoctorList"
                >เลือกทั้งหมด</b-button
              >
            </b-input-group-append>
          </b-input-group>
        </b-form-group> </b-col
      ><b-col cols="12" md="4" class="mb-1">
        <date-picker
          class="col-12 mb-2 px-0"
          v-model="dateRange"
          type="date"
          range
          format="DD MMM YY"
          :shortcuts="dateRangeShortcuts"
          :disabled-date="notAfterToday"
          placeholder="เลือกวันที่ต้องการค้นหา"
          input-class="form-control"
        >
        </date-picker>

        <b-button
          class="float-right mb-2 ml-2"
          variant="primary"
          @click="fetchReport"
        >
          แสดงข้อมูล
        </b-button>
        <b-button
          class="col-auto float-right"
          variant="info"
          @click="excelExport"
        >
          <i class="fas fa-file-excel"></i
        ></b-button>
      </b-col>
    </b-row>
    <b-tabs pills>
      <b-tab title="สรุปรวม" class="px-0">
        <h6 class="mt-2">
          รายงานของวันที่
          {{ formatDate(dateRange[0]) + " - " + formatDate(dateRange[1]) }}
        </h6>
        <b-table
          id="doctors-summary-table"
          class="mt-2"
          small
          sort-icon-left
          head-variant="light"
          :busy="tableBusy"
          :items="filteredDoctorIncomeList"
          :fields="visibleSumFields"
          :responsive="true"
          show-empty
          sticky-header="calc(100vh - 210px)"
        >
          <template #cell(index)="row">{{ row.index + 1 }}.</template>
          <template #cell(calculatedFee)="row">
            ({{ formatNumber(getTotalFeeForDoctor(row.item.uid)) }})
          </template>
          <template #cell(calculatedNetDf)="row">
            {{ formatNumber(row.item.sumReport.paidDf - getTotalFeeForDoctor(row.item.uid) - row.item.sumReport.labDfAmount) }}
          </template>
        </b-table>
      </b-tab>
      <b-tab title="รายงานตามวัน" class="px-0"
        ><div id="doctorsIncomeTable" ref="doctorsIncomeTable" class="mt-2">
          <div v-for="doctor in filteredDoctorIncomeList" :key="doctor.uid">
            <h6>แพทย์: {{ doctor.user.fullName }}</h6>

            <!-- Income Table -->
            <b-table
              :id="'doctors-report-' + doctor.uid"
              small
              sort-icon-left
              head-variant="light"
              :busy="tableBusy"
              :items="doctor.reportDoctorIncomeList"
              :fields="visibleDailyFields"
              sort-by="creationDt"
              v-model="visibleRows"
              :sort-desc="true"
              :responsive="true"
              show-empty
              sticky-header="calc(100vh - 210px)"
            >
              <template #cell(index)="row">{{ row.index + 1 }}.</template>
              <template #cell(creationDt)="row">{{
                formatDate(row.value)
              }}</template>
              <template #cell(dfNet)="row">{{
                row.item.paidDf - row.item.labDfAmount
              }}</template>
              <template #custom-foot>
                <!-- Old layout (before cutoff) -->
                <b-tr v-if="!isAfterCutoff" variant="secondary" class="text-bold text-right">
                  <td class="text-left">รวม</td>
                  <td></td>
                  <td></td>
                  <td></td>
                  <td></td>
                  <td>{{ doctor.sumReport.paid }}</td>
                  <td></td>
                  <td>{{ doctor.sumReport.processingFeeBaht }}</td>
                  <td>{{ doctor.sumReport.paidDf }}</td>
                  <td>{{ doctor.sumReport.paidCf }}</td>
                  <td>{{ doctor.sumReport.labDfAmount }}</td>
                  <td>{{ doctor.sumReport.labCfAmount }}</td>
                  <td>{{ doctor.sumReport.totalReceived }}</td>
                </b-tr>
                <!-- New layout (after cutoff) -->
                <b-tr v-else variant="secondary" class="text-bold text-right">
                  <td class="text-left">รวม DF</td>
                  <td></td>
                  <td></td>
                  <td></td>
                  <td></td>
                  <td>{{ formatNumber(doctor.sumReport.paid) }}</td>
                  <td></td>
                  <td>{{ formatNumber(doctor.sumReport.paidDf) }}</td>
                </b-tr>
              </template>
              <template #empty="">
                <p class="text-center text-muted my-2">ไม่มีข้อมูลให้แสดง</p>
              </template>
            </b-table>

            <!-- Fee Summary Section (after cutoff only) -->
            <b-card v-if="isAfterCutoff && getDfByPaymentForDoctor(doctor.uid).length > 0" class="mt-3" no-body>
              <b-card-header class="py-2">
                <strong>สรุปค่าธรรมเนียมตามวิธีชำระ</strong>
              </b-card-header>
              <b-card-body class="p-0">
                <b-table-simple small responsive hover>
                  <b-thead head-variant="light">
                    <b-tr class="text-right">
                      <b-th class="text-left">วิธีชำระ</b-th>
                      <b-th>DF</b-th>
                      <b-th>ค่าธรรมเนียม %</b-th>
                      <b-th>ค่าธรรมเนียม</b-th>
                    </b-tr>
                  </b-thead>
                  <b-tbody>
                    <b-tr v-for="(pm, idx) in getDfByPaymentForDoctor(doctor.uid)" :key="idx" class="text-right">
                      <b-td class="text-left">{{ pm.name }}</b-td>
                      <b-td>{{ formatNumber(pm.df) }}</b-td>
                      <b-td>{{ pm.feePercent }}%</b-td>
                      <b-td class="text-danger">({{ formatNumber(pm.feeAmount) }})</b-td>
                    </b-tr>
                  </b-tbody>
                  <b-tfoot>
                    <b-tr variant="secondary" class="text-bold text-right">
                      <b-td class="text-left">รวมค่าธรรมเนียม</b-td>
                      <b-td></b-td>
                      <b-td></b-td>
                      <b-td class="text-danger">({{ formatNumber(getTotalFeeForDoctor(doctor.uid)) }})</b-td>
                    </b-tr>
                  </b-tfoot>
                </b-table-simple>
              </b-card-body>
            </b-card>

            <!-- Lab Summary Section (after cutoff only) -->
            <b-card v-if="isAfterCutoff" class="mt-3" no-body>
              <b-card-header class="py-2">
                <strong>สรุปค่าแลป</strong>
              </b-card-header>
              <b-card-body class="py-2">
                <b-row>
                  <b-col class="text-right">
                    <span>รวมค่าแลป DF: </span>
                    <strong class="text-danger">({{ formatNumber(doctor.sumReport.labDfAmount) }})</strong>
                  </b-col>
                </b-row>
              </b-card-body>
            </b-card>

            <!-- Total Summary Section (after cutoff only) -->
            <b-card v-if="isAfterCutoff" class="mt-3 bg-light" no-body>
              <b-card-body class="py-3">
                <b-row class="text-right">
                  <b-col>
                    <p class="mb-1">
                      DF: <strong>{{ formatNumber(doctor.sumReport.paidDf) }}</strong>
                      - ค่าธรรมเนียม: <strong class="text-danger">{{ formatNumber(getTotalFeeForDoctor(doctor.uid)) }}</strong>
                      - ค่าแลป: <strong class="text-danger">{{ formatNumber(doctor.sumReport.labDfAmount) }}</strong>
                      = DF สุทธิ: <strong class="text-success">{{ formatNumber(doctor.sumReport.paidDf - getTotalFeeForDoctor(doctor.uid) - doctor.sumReport.labDfAmount) }}</strong>
                    </p>
                  </b-col>
                </b-row>
              </b-card-body>
            </b-card>

            <hr class="my-4" />
          </div></div
      ></b-tab>
    </b-tabs>

    <!-- <div class="d-none">
      <div ref="header" id="printHeader">
        <h4>รายงานรายได้แพทย์ทุกท่าน</h4>
        <h6>
          วันที่
          {{
            formatDate(dateRange.startDate) +
            "-" +
            formatDate(dateRange.endDate)
          }}
        </h6>
        <span class="text-right">
          พิมพ์วันที่ {{ formatDate(today) }} จัดทำโดย
          {{ userInfo.firstNameTh + " " + userInfo.lastNameTh }}</span
        >
      </div>
      <div ref="footer" id="printFooter">
        <h3>Footer</h3>
      </div>
    </div> -->
    <!-- <PdfExport ref="pdfExport" /> -->
    <Dialog ref="Dialog"></Dialog>
    <Loading v-if="isLoading"></Loading>
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import { eventBus } from "@/main";

// import PdfExport from "@/components/PdfExport";

import Dialog from "@/components/modal/Dialog";
import Loading from "@/components/Loading";
import moment from "moment";
import XLSX from "xlsx";

export default {
  name: "BranchItemsIncomeReport",
  components: {
    Dialog,
    Loading,
  },
  data() {
    return {
      isLoading: false,

      dateRange: [],
      selectedDoctor: [],

      //table
      tableBusy: false,
      doctorsIncomeList: [],
      fields: [
        {
          key: "index",
          label: "",
          class: "narrow-spacing",
        },
        {
          key: "creationDt",
          label: "วันที่",
          sortable: true,
          class: "narrow-spacing",
        },
        {
          key: "price",
          label: "ค่ารักษา",
          class: "text-right narrow-spacing",
        },
        {
          key: "finalDiscount",
          label: "ส่วนลด",
          class: "text-right narrow-spacing",
        },
        {
          key: "amount",
          label: "ค่ารักษาสุทธิ",
          class: "text-right narrow-spacing",
        },
        {
          key: "paid",
          label: "ชำระ",
          class: "text-right narrow-spacing",
        },
        {
          key: "overdue",
          label: "ค้างชำระ",
          class: "text-right text-danger narrow-spacing",
        },
        {
          key: "processingFeeBaht",
          label: "ค่าธรรมเนียม",
          class: "text-right narrow-spacing",
        },
        {
          key: "paidDf",
          label: "DF",
          class: "text-right narrow-spacing",
        },
        {
          key: "paidCf",
          label: "CF",
          class: "text-right narrow-spacing",
        },
        {
          key: "labDfAmount",
          label: "Lab DF",
          class: "text-right narrow-spacing",
        },
        {
          key: "labCfAmount",
          label: "Lab CF",
          class: "text-right narrow-spacing",
        },
        {
          key: "dfNet",
          label: "DF สุทธิ",
          class: "text-right narrow-spacing font-weight-bold",
        },
      ],
      visibleRows: [],
      sumFields: [
        {
          key: "index",
          label: "",
          class: "narrow-spacing",
        },
        {
          key: "user.fullName",
          label: "แพทย์",
          sortable: true,
          class: "narrow-spacing",
        },

        {
          key: "sumReport.paid",
          label: "ชำระ",
          class: "text-right narrow-spacing",
        },

        {
          key: "sumReport.processingFeeBaht",
          label: "ค่าธรรมเนียม",
          class: "text-right narrow-spacing",
        },
        {
          key: "sumReport.paidDf",
          label: "DF",
          class: "text-right narrow-spacing",
        },
        {
          key: "sumReport.paidCf",
          label: "CF",
          class: "text-right narrow-spacing",
        },
        {
          key: "sumReport.labDfAmount",
          label: "Lab DF",
          class: "text-right narrow-spacing",
        },
        {
          key: "sumReport.labCfAmount",
          label: "Lab CF",
          class: "text-right narrow-spacing",
        },
        {
          key: "sumReport.totalReceived",
          label: "DF สุทธิ",
          class: "text-right narrow-spacing font-weight-bold",
        },
      ],
      sumKeys: ["paid", "processingFeeBaht", "paidDf", "paidCf", "labDfAmount", "labCfAmount", "labTotalExpense"],

      loadingTime: 0,

      // DF by payment type data (for after cutoff)
      dfByPaymentData: [],
    };
  },
  created() {
    this.init();
  },
  computed: {
    ...mapGetters({
      userInfo: "moduleUser/getUserInfo",
      getDoctorList: "moduleUser/getDoctorList",
      getReportDateRange: "moduleReport/getReportDateRange",
      dateRangeShortcuts: "moduleMaster/getDateRangeShortcuts",
      paymentMethodList: "moduleUser/getPaymentMethodList",
    }),
    dateFrom() {
      return this.dateRange[0]
        ? moment(this.dateRange[0]).startOf("day").format()
        : "";
    },
    dateTo() {
      return this.dateRange[1]
        ? moment(this.dateRange[1]).endOf("day").format()
        : "";
    },
    today() {
      let today = new Date();
      today.setHours(0, 0, 0, 0);
      return today;
    },
    filteredDoctorIncomeList() {
      let listAllDoctors = this.doctorsIncomeList;
      listAllDoctors.forEach(doctor => {
        doctor.sumReport = this.sumKeys.reduce((accumulator, value) => {
          return { ...accumulator, [value]: 0 };
        }, {});

        this.sumKeys.forEach(field => {
          doctor.sumReport[field] = this.calculateSum(
            doctor.reportDoctorIncomeList,
            field
          );
        });

        // Calculate total amount doctor receives (paidDf - labDfAmount)
        doctor.sumReport.totalReceived = 
          doctor.sumReport.paidDf - 
          doctor.sumReport.labDfAmount;
      });
      let selectedUid = this.selectedDoctor.map(i => i.uid);
      return listAllDoctors.filter(doctor => selectedUid.includes(doctor.uid));
    },

    // Check if date range is after cutoff (1 Feb 2026)
    isAfterCutoff() {
      if (!this.dateRange || this.dateRange.length !== 2) return false;
      const cutoff = moment("2026-02-01").startOf("day");
      return moment(this.dateRange[0]).startOf("day").isSameOrAfter(cutoff);
    },

    // Check if date range crosses cutoff
    isCrossDateRange() {
      if (!this.dateRange || this.dateRange.length !== 2) return false;
      const cutoff = moment("2026-02-01").startOf("day");
      const start = moment(this.dateRange[0]).startOf("day");
      const end = moment(this.dateRange[1]).startOf("day");
      return start.isBefore(cutoff) && end.isSameOrAfter(cutoff);
    },

    // Visible fields for summary table based on date
    visibleSumFields() {
      if (this.isAfterCutoff) {
        return [
          { key: "index", label: "", class: "narrow-spacing" },
          { key: "user.fullName", label: "แพทย์", sortable: true, class: "narrow-spacing" },
          { key: "sumReport.paid", label: "ชำระ", class: "text-right narrow-spacing" },
          { key: "sumReport.paidDf", label: "DF", class: "text-right narrow-spacing" },
          { key: "calculatedFee", label: "ค่าธรรมเนียม", class: "text-right narrow-spacing text-danger" },
          { key: "sumReport.labDfAmount", label: "ค่าแลป", class: "text-right narrow-spacing text-danger" },
          { key: "calculatedNetDf", label: "DF สุทธิ", class: "text-right narrow-spacing font-weight-bold" },
        ];
      }
      return this.sumFields;
    },

    // Visible fields for daily table based on date
    visibleDailyFields() {
      if (this.isAfterCutoff) {
        return this.fields.filter(f =>
          !["processingFeeBaht", "paidCf", "labCfAmount", "dfNet"].includes(f.key)
        );
      }
      return this.fields;
    },

    // Get DF by payment data for a specific doctor
    getDfByPaymentForDoctor() {
      return (doctorUid) => {
        const doctorData = this.dfByPaymentData.find(d => d.doctorUid === doctorUid);
        if (!doctorData) return [];

        const paymentTotals = {};
        doctorData.dates?.forEach(date => {
          date.dfByPaymentType?.forEach(pm => {
            if (pm.value > 0) {
              if (!paymentTotals[pm.keyName]) {
                paymentTotals[pm.keyName] = {
                  name: pm.name,
                  keyName: pm.keyName,
                  df: 0,
                  feePercent: this.getProcessingFee(pm.keyName),
                };
              }
              paymentTotals[pm.keyName].df += pm.value;
            }
          });
        });

        return Object.values(paymentTotals).map(pm => ({
          ...pm,
          feeAmount: (pm.df * pm.feePercent) / 100,
          netDf: pm.df - (pm.df * pm.feePercent) / 100,
        }));
      };
    },

    // Calculate total fee for a doctor from DfByPaymentType data
    getTotalFeeForDoctor() {
      return (doctorUid) => {
        const paymentData = this.getDfByPaymentForDoctor(doctorUid);
        return paymentData.reduce((sum, pm) => sum + pm.feeAmount, 0);
      };
    },
  },
  watch: {},
  methods: {
    ...mapActions({
      fetchReportBranchDoctorIncome:
        "moduleReport/fetchReportBranchDoctorIncome",
      fetchDfByPaymentType: "moduleReport/fetchDfByPaymentType",
      fetchPaymentMethodList: "moduleUser/fetchPaymentMethodList",
      setStateReportDateRange: "moduleReport/setStateReportDateRange",
    }),
    init() {
      this.fetchPaymentMethodList({
        clinicUrl: this.$route.params.clinicUrl,
        branchUrl: this.$route.params.branchUrl,
      });
    },
    moment() {
      return moment();
    },
    getProcessingFee(keyName) {
      const method = this.paymentMethodList.find(m => m.id === keyName);
      return method?.processingFee || 0;
    },
    formatNumber(num) {
      if (num === null || num === undefined) return "-";
      return num.toLocaleString("th-TH", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },
    formatDate(date) {
      if (date) {
        return moment(date).locale("th").format("D MMM YY");
      } else {
        return "";
      }
    },
    resetDateRange() {
      this.dateRange = {
        startDate: null,
        endDate: null,
      };
    },
    showDialogToast(variant, textDetail) {
      this.$refs.Dialog.showToast(variant, textDetail);
    },
    sum(arg, row) {
      let num = row.reduce((accum, item) => {
        return accum + parseFloat(item[arg]);
      }, 0.0);
      return Math.round(num * 100) / 100;
    },
    calculateSum(array, property) {
      const total = array.reduce((accumulator, object) => {
        return accumulator + object[property];
      }, 0);

      return total;
    },
    sendEmail() {
      //รอ api
    },
    async fetchReport() {
      if (this.dateFrom === "" || this.dateTo === "") return;

      // Block cross-cutoff date range
      if (this.isCrossDateRange) {
        this.showDialogToast("warning", "ไม่สามารถ query ข้ามช่วง 1 ก.พ. 69 ได้ กรุณาเลือกช่วงวันที่ใหม่");
        return;
      }

      this.isLoading = true;
      this.setStateReportDateRange(this.dateRange);

      try {
        // Fetch main income report
        const res = await this.fetchReportBranchDoctorIncome({
          clinicUrl: this.$route.params.clinicUrl,
          branchUrl: this.$route.params.branchUrl,
          dateFrom: this.dateFrom,
          dateTo: this.dateTo,
        });
        this.doctorsIncomeList = res.data.report;

        // If after cutoff, also fetch DF by payment type
        if (this.isAfterCutoff) {
          const dfRes = await this.fetchDfByPaymentType({
            clinicUrl: this.$route.params.clinicUrl,
            branchUrl: this.$route.params.branchUrl,
            dateFrom: this.dateFrom,
            dateTo: this.dateTo,
            groupBy: "day",
          });
          this.dfByPaymentData = dfRes.data.item || [];
        } else {
          this.dfByPaymentData = [];
        }
      } catch (err) {
        console.error(err);
      } finally {
        this.isLoading = false;
      }
    },
    excelExport() {
      document.querySelectorAll(".sr-only").forEach(e => e.remove());
      let wb = XLSX.utils.book_new();
      let filename = "report.xlsx";

      let tbl = document.getElementById("doctors-summary-table");
      tbl.insertRow(0);
      tbl.insertRow(0);
      let ws = XLSX.utils.table_to_sheet(tbl);
      XLSX.utils.sheet_add_aoa(ws, [
        [`วันที่: ${this.dateFrom} - ${this.dateTo}`],
      ]);
      XLSX.utils.book_append_sheet(wb, ws, "สรุปรวม");

      this.filteredDoctorIncomeList.forEach(i => {
        let id = "doctors-report-" + i.uid;
        let tbl = document.getElementById(id).cloneNode(true);
        tbl.insertRow(0);
        tbl.insertRow(0);
        tbl.insertRow(0);

        let ws = XLSX.utils.table_to_sheet(tbl);

        XLSX.utils.sheet_add_aoa(ws, [
          [`แพทย์: ${i.user.fullName}`],
          [`วันที่: ${this.dateFrom} - ${this.dateTo}`],
        ]);

        XLSX.utils.book_append_sheet(wb, ws, i.user.fullName.slice(0, 30));
      });

      XLSX.writeFile(wb, filename, {
        // type: "string",
      });
      return;
    },
    notAfterToday(date) {
      return date > new Date(new Date().setHours(0, 0, 0, 0));
    },
  },
  mounted() {
    this.selectedDoctor = this.getDoctorList;

    if (this.getReportDateRange[0] && this.getReportDateRange[1]) {
      this.dateRange = this.getReportDateRange;
    } else {
      this.dateRange = [this.today, this.today];
    }
  },
  updated() {},
};
</script>

<style>
</style>
